import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback, useMemo,
} from "react";
import config from "../config";
import { useAuth } from "./AuthContext";

const Ctx = createContext({
  workingChatbotIds: new Set(),
  workingHistoryIds: new Set(),
});

const wsUrl = () => {
  const base = config.api.baseUrl.replace(/^http/, "ws");
  return `${base}/chatbot/user-status`;
};

export function UserStatusProvider({ children }) {
  const { user } = useAuth();
  const [workingChatbotIds, setChatbotIds] = useState(() => new Set());
  const [workingHistoryIds, setHistoryIds] = useState(() => new Set());
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const backoffMs = useRef(500);

  const replaceFromSnapshot = useCallback((runs) => {
    const cs = new Set();
    const hs = new Set();
    for (const r of runs || []) {
      if (r.chatbot_id) cs.add(String(r.chatbot_id));
      if (r.history_id) hs.add(String(r.history_id));
    }
    setChatbotIds(cs);
    setHistoryIds(hs);
  }, []);

  const addPair = useCallback((chatbot_id, history_id) => {
    if (chatbot_id) {
      setChatbotIds((prev) => {
        const next = new Set(prev); next.add(String(chatbot_id)); return next;
      });
    }
    if (history_id) {
      setHistoryIds((prev) => {
        const next = new Set(prev); next.add(String(history_id)); return next;
      });
    }
  }, []);

  const removePair = useCallback((chatbot_id, history_id) => {
    if (history_id) {
      setHistoryIds((prev) => {
        const next = new Set(prev); next.delete(String(history_id)); return next;
      });
    }
    // We can't safely remove the chatbot_id without knowing if other histories
    // under that bot are still active. Reseed via the next snapshot or rely on
    // the next reconnect to correct it. For correctness in the common case,
    // also remove it — Layout will only show false for ~1s at worst.
    if (chatbot_id) {
      setChatbotIds((prev) => {
        const next = new Set(prev); next.delete(String(chatbot_id)); return next;
      });
    }
  }, []);

  useEffect(() => {
    if (!user?.email) return undefined;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      let ws;
      try {
        ws = new WebSocket(wsUrl());
      } catch (_) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => { backoffMs.current = 500; };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "snapshot") {
            replaceFromSnapshot(msg.runs);
          } else if (msg.type === "run_started") {
            addPair(msg.chatbot_id, msg.history_id);
          } else if (
            msg.type === "run_completed"
            || msg.type === "run_failed"
            || msg.type === "run_cancelled"
          ) {
            removePair(msg.chatbot_id, msg.history_id);
          }
        } catch (_) { /* ignore */ }
      };

      ws.onclose = () => { if (!cancelled) scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch (_) { /* ignore */ } };
    };

    const scheduleReconnect = () => {
      const delay = Math.min(backoffMs.current, 10000);
      backoffMs.current = Math.min(backoffMs.current * 2, 10000);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer.current);
      try { wsRef.current?.close(); } catch (_) { /* ignore */ }
      wsRef.current = null;
    };
  }, [user?.email, replaceFromSnapshot, addPair, removePair]);

  const value = useMemo(
    () => ({ workingChatbotIds, workingHistoryIds }),
    [workingChatbotIds, workingHistoryIds],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIsChatbotWorking(chatbotId) {
  const { workingChatbotIds } = useContext(Ctx);
  if (!chatbotId) return false;
  return workingChatbotIds.has(String(chatbotId));
}

export function useIsHistoryWorking(historyId) {
  const { workingHistoryIds } = useContext(Ctx);
  if (!historyId) return false;
  return workingHistoryIds.has(String(historyId));
}

// Exposed for Layout's parent-tab bubble-up logic.
export function useWorkingChatbotIdSet() {
  return useContext(Ctx).workingChatbotIds;
}

export function useWorkingHistoryIdSet() {
  return useContext(Ctx).workingHistoryIds;
}
